const SYMBOLS: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', INR: '₹' };

// ── Private mode ──────────────────────────────────────────────────────
//
// Every figure in the app goes through the formatters below, so masking here
// covers the whole surface — cards, tables, charts, aria labels — without each
// view needing to know about it. The flag is a module global rather than
// context because these are plain functions called from ~90 render sites;
// `PrivacyProvider` owns it and re-renders the view when it flips.

/**
 * Fixed width on purpose. A mask that tracked the digit count would leak the
 * magnitude — the thing most worth hiding.
 */
const MASK = '••••';

let masked = false;

export function setMoneyMasked(next: boolean): void {
  masked = next;
}

export function isMoneyMasked(): boolean {
  return masked;
}

/**
 * Redacts currency amounts inside free text. The advisor's replies and the
 * activity log are model-written prose, so they carry real figures that never
 * touched `money()`.
 *
 * Deliberately only matches amounts carrying a currency marker: masking bare
 * numbers would eat years, counts and percentages, and the model reliably
 * writes amounts with a symbol.
 */
const TEXT_AMOUNT = /(?:[$€£₹]|\b(?:USD|EUR|GBP|INR)\s?)-?\d[\d,]*(?:\.\d+)?/g;

export function maskText(text: string): string {
  if (!masked) return text;
  return text.replace(TEXT_AMOUNT, (match) => `${match.match(/^[^\d-]+/)?.[0] ?? ''}${MASK}`);
}

/**
 * Formats cents for display. Uses a true minus sign (−) rather than a hyphen,
 * matching the design and reading better in the mono figures.
 */
export function money(cents: number, currency = 'USD', decimals = false): string {
  const symbol = SYMBOLS[currency] ?? `${currency} `;
  const sign = cents < 0 ? '−' : '';
  // The sign stays visible while masked: a credit balance reading as an asset
  // would misinform rather than conceal, and direction gives away no amount.
  if (masked) return `${sign}${symbol}${MASK}`;
  const value = Math.abs(cents) / 100;
  const formatted = value.toLocaleString('en-US', {
    minimumFractionDigits: decimals ? 2 : 0,
    maximumFractionDigits: decimals ? 2 : 0,
  });
  return `${sign}${symbol}${formatted}`;
}

/** Signed variant for ledger rows, where a leading + carries meaning. */
export function signedMoney(cents: number, currency = 'USD', decimals = true): string {
  return `${cents > 0 ? '+' : ''}${money(cents, currency, decimals)}`;
}

export function compactMoney(cents: number, currency = 'USD'): string {
  const symbol = SYMBOLS[currency] ?? `${currency} `;
  const value = Math.abs(cents) / 100;
  const sign = cents < 0 ? '−' : '';
  if (masked) return `${sign}${symbol}${MASK}`;
  if (value >= 1_000_000) return `${sign}${symbol}${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${sign}${symbol}${(value / 1_000).toFixed(1)}k`;
  return `${sign}${symbol}${Math.round(value)}`;
}

export function percent(value: number, decimals = 0): string {
  return `${value.toFixed(decimals)}%`;
}

export function signedPercent(value: number | null): string {
  if (value === null) return 'new';
  return `${value >= 0 ? '+' : '−'}${Math.abs(Math.round(value))}%`;
}

/** "08/26" — the compact form used in the transactions table. */
export function shortDate(iso: string): string {
  return iso.slice(5).replace('-', '/');
}

export function longDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMinutes = Math.round((Date.now() - then) / 60_000);
  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const hours = Math.round(diffMinutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function greeting(name: string): string {
  const hour = new Date().getHours();
  const part = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  return `Good ${part}, ${name.split(' ')[0]}`;
}

/** Today on the user's own calendar — pairs with `nowTime()` in the record form. */
export function localTodayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** "14:05" on the user's clock. */
export function nowTime(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
